import BaseApiService from './BaseApiService';

/**
 * User API Service
 * 
 * Handles general user-related API calls including:
 * - User profile management
 * - User data retrieval
 * - User activity tracking
 * - User content management
 * 
 * @class UserApiService
 * @extends BaseApiService
 */
class UserApiService extends BaseApiService {
  /**
   * Gets complete user profile information
   * @returns {Promise<Object>} Complete user profile data
   */
  async getUserProfile() {
    return await this.get('/user/profile');
  }

  /**
   * Updates user profile
   * @param {Object} profileData - Profile data to update
   * @returns {Promise<Object>} Updated profile data
   */
  async updateUserProfile(profileData) {
    return await this.put('/user/profile', profileData);
  }

  /**
   * Gets user's exercise history
   * @param {number} [limit=50] - Number of records to retrieve
   * @param {number} [offset=0] - Offset for pagination
   * @returns {Promise<Array>} User's exercise history
   */
  async getExerciseHistory(limit = 50, offset = 0) {
    return await this.get(`/user/exercise-history?limit=${limit}&offset=${offset}`);
  }

  /**
   * Records a completed exercise
   * @param {Object} exerciseData - Exercise completion data
   * @param {number} exerciseData.exerciseId - ID of the exercise
   * @param {number} exerciseData.duration - Duration in minutes
   * @param {string} exerciseData.completedAt - ISO timestamp
   * @param {string} [exerciseData.notes] - Optional notes
   * @returns {Promise<Object>} Recording confirmation
   */
  async recordExerciseCompletion(exerciseData) {
    return await this.post('/user/exercise-completion', exerciseData);
  }

  /**
   * Gets user's progress statistics
   * @param {string} [period='month'] - Period for statistics ('week', 'month', 'year', 'all')
   * @returns {Promise<Object>} Progress statistics
   */
  async getProgressStats(period = 'month') {
    return await this.get(`/user/progress?period=${period}`);
  }

  /**
   * Gets user's achievements and milestones
   * @returns {Promise<Array>} List of achievements
   */
  async getAchievements() {
    return await this.get('/user/achievements');
  }

  /**
   * Gets user's streak information
   * @returns {Promise<Object>} Streak data including current and longest streaks
   */
  async getStreakInfo() {
    return await this.get('/user/streak');
  }

  /**
   * Gets user's favorite exercises
   * @returns {Promise<Array>} List of favorite exercises
   */
  async getFavoriteExercises() {
    return await this.get('/user/favorites');
  }

  /**
   * Adds an exercise to favorites
   * @param {number} exerciseId - ID of the exercise to favorite
   * @returns {Promise<Object>} Addition confirmation
   */
  async addToFavorites(exerciseId) {
    return await this.post('/user/favorites', { exerciseId });
  }

  /**
   * Removes an exercise from favorites
   * @param {number} exerciseId - ID of the exercise to remove
   * @returns {Promise<Object>} Removal confirmation
   */
  async removeFromFavorites(exerciseId) {
    return await this.delete(`/user/favorites/${exerciseId}`);
  }

  /**
   * Gets user's custom exercises (if supported)
   * @returns {Promise<Array>} List of custom exercises
   */
  async getCustomExercises() {
    return await this.get('/user/custom-exercises');
  }

  /**
   * Creates a custom exercise
   * @param {Object} exerciseData - Custom exercise data
   * @param {string} exerciseData.name - Exercise name
   * @param {string} exerciseData.description - Exercise description
   * @param {number} exerciseData.duration - Duration in minutes
   * @param {string} [exerciseData.category] - Exercise category
   * @param {Array<string>} [exerciseData.tags] - Exercise tags
   * @returns {Promise<Object>} Created exercise data
   */
  async createCustomExercise(exerciseData) {
    return await this.post('/user/custom-exercises', exerciseData);
  }

  /**
   * Updates a custom exercise
   * @param {number} exerciseId - ID of the exercise to update
   * @param {Object} exerciseData - Updated exercise data
   * @returns {Promise<Object>} Updated exercise data
   */
  async updateCustomExercise(exerciseId, exerciseData) {
    return await this.put(`/user/custom-exercises/${exerciseId}`, exerciseData);
  }

  /**
   * Deletes a custom exercise
   * @param {number} exerciseId - ID of the exercise to delete
   * @returns {Promise<Object>} Deletion confirmation
   */
  async deleteCustomExercise(exerciseId) {
    return await this.delete(`/user/custom-exercises/${exerciseId}`);
  }

  /**
   * Gets user's feedback submissions
   * @param {number} [limit=20] - Number of records to retrieve
   * @returns {Promise<Array>} List of feedback submissions
   */
  async getFeedbackHistory(limit = 20) {
    return await this.get(`/user/feedback?limit=${limit}`);
  }

  /**
   * Submits user feedback
   * @param {Object} feedbackData - Feedback data
   * @param {string} feedbackData.type - Feedback type ('bug', 'feature', 'general')
   * @param {string} feedbackData.subject - Feedback subject
   * @param {string} feedbackData.message - Feedback message
   * @param {number} [feedbackData.rating] - Rating (1-5)
   * @returns {Promise<Object>} Submission confirmation
   */
  async submitFeedback(feedbackData) {
    return await this.post('/user/feedback', feedbackData);
  }

  /**
   * Gets user's notification preferences
   * @returns {Promise<Object>} Notification preferences
   */
  async getNotificationPreferences() {
    return await this.get('/user/notification-preferences');
  }

  /**
   * Updates notification preferences
   * @param {Object} preferences - Notification preferences
   * @returns {Promise<Object>} Update confirmation
   */
  async updateNotificationPreferences(preferences) {
    return await this.put('/user/notification-preferences', preferences);
  }

  /**
   * Gets user's goal settings
   * @returns {Promise<Object>} Goal settings and progress
   */
  async getGoals() {
    return await this.get('/user/goals');
  }

  /**
   * Sets or updates user goals
   * @param {Object} goalData - Goal settings
   * @param {number} goalData.dailyExercises - Daily exercise goal
   * @param {number} goalData.weeklyMinutes - Weekly minutes goal
   * @param {Array<string>} goalData.preferredDays - Preferred exercise days
   * @returns {Promise<Object>} Goal update confirmation
   */
  async setGoals(goalData) {
    return await this.put('/user/goals', goalData);
  }

  /**
   * Gets user's dashboard summary data
   * @returns {Promise<Object>} Dashboard summary with key metrics
   */
  async getDashboardSummary() {
    return await this.get('/user/dashboard');
  }

  /**
   * Gets user's calendar events and scheduled activities
   * @param {string} startDate - Start date (ISO string)
   * @param {string} endDate - End date (ISO string)
   * @returns {Promise<Array>} Calendar events
   */
  async getCalendarEvents(startDate, endDate) {
    const params = new URLSearchParams({ startDate, endDate });
    return await this.get(`/user/calendar?${params.toString()}`);
  }

  /**
   * Gets user's recommended exercises based on history and preferences
   * @param {number} [limit=10] - Number of recommendations
   * @returns {Promise<Array>} Recommended exercises
   */
  async getRecommendedExercises(limit = 10) {
    return await this.get(`/user/recommendations?limit=${limit}`);
  }

  /**
   * Records user interaction for analytics
   * @param {Object} interactionData - Interaction data
   * @param {string} interactionData.action - Action performed
   * @param {string} interactionData.component - Component involved
   * @param {Object} [interactionData.metadata] - Additional metadata
   * @returns {Promise<Object>} Recording confirmation
   */
  async recordInteraction(interactionData) {
    return await this.post('/user/interactions', interactionData);
  }
}

// Create and export a singleton instance
const userApiService = new UserApiService();
export default userApiService; 